import React, { useRef, useState } from "react";
import { FormGroup } from "reactstrap";
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useDropzone } from "react-dropzone";

import uploadFileIcon from "../../assets/images/app/allaya/file-upload.svg";
import modalCloseIcon from "../../assets/images/logo/modal-close-icon.png";

import {
    allowedMediaExtensions,
    allowedMediaSize,
    fileUploadHandler,
    formatFileSize
} from "../../utils/mediaUtils";
import { MediaType } from "../../constants";
import { CustomModal } from "../CustomModal";
import setCanvasPreview from "../../setCanvasPreview";
import { CustomButton } from "../CustomButton";
import { truncateFileName } from "../../utils/commonUtils";

const MIN_DIMENSION = 150;
const ASPECT_RATIO = 1;

const FileUploader = ({
    uploadText = "Drag file here or ",
    fileType = MediaType.IMAGE,
    supportedFormats = "Supported formats",
    onFileUpload = () => {},
    onFileRemove = () => {},
    acceptedFileTypes = [],
    previewUrl = null, // New prop for preview
    previewSize = 0,
    shouldCrop = false // New prop to control cropping
}) => {
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const [uploadedFile, setUploadedFile] = useState(
        previewUrl
            ? {
                  url: previewUrl,
                  name: previewUrl.split("/").pop(),
                  size: previewSize
              }
            : null
    );
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [imgSrc, setImgSrc] = useState("");
    const [crop, setCrop] = useState();
    const [modalOpen, setModalOpen] = useState(false);
    const [isCropConfirmed, setIsCropConfirmed] = useState(false); // Flag for crop confirmation

    const removeFile = () => {
        setUploadedFile(null);
        onFileRemove();
    };

    // Dropzone configuration
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: acceptedFileTypes.map((ext) => `.${ext}`).join(","), // Convert array to format ".jpg,.png"
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleFileInputChange({ target: { files: acceptedFiles } });
            }
        },
        onDropRejected: () => setError("Invalid file type or size exceeded.")
    });

    const handleFileInputChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file) {
            fileUploadHandler({
                file,
                mediaType: fileType,
                errorCB: setError,
                previewCB: (preview) => {
                    if (preview) {
                        setUploadedFile({
                            name: file.name,
                            size: formatFileSize(file.size),
                            type: file.type,
                            url: preview
                        });

                        // If cropping is disabled, directly pass the image
                        if (!shouldCrop) {
                            onFileUpload({
                                name: file.name,
                                size: formatFileSize(file.size),
                                type: file.type,
                                url: preview
                            });
                        }
                    }
                },
                uploadFileCB: (validatedFile) => {
                    if (validatedFile) {
                        setError("");

                        if (fileType === MediaType.AUDIO) {
                            // Handle audio file
                            const audioURL = URL.createObjectURL(validatedFile);
                            const audio = new Audio(audioURL);

                            audio.onloadedmetadata = () => {
                                const duration = parseFloat(audio.duration.toFixed(2)); // Get and format duration

                                onFileUpload(
                                    {
                                        name: validatedFile.name,
                                        size: formatFileSize(validatedFile.size),
                                        type: validatedFile.type,
                                        lastModified: validatedFile.lastModified,
                                        webkitRelativePath: validatedFile.webkitRelativePath,
                                        duration: duration // Use parsed number for duration
                                    },
                                    file
                                );

                                // Revoke the object URL and cleanup
                                setTimeout(() => URL.revokeObjectURL(audioURL), 0);
                            };

                            audio.onerror = () => {
                                setError("Failed to load audio file. Please try again.");
                                // Revoke the object URL on error
                                URL.revokeObjectURL(audioURL);
                            };
                        } else if (fileType === MediaType.IMAGE) {
                            setFile(file);
                            const reader = new FileReader();
                            reader.addEventListener("load", () => {
                                const imageElement = new Image();
                                const imageUrl = reader.result?.toString() || "";

                                imageElement.src = imageUrl;

                                imageElement.addEventListener("load", (e) => {
                                    if (error) setError("");
                                    const { naturalWidth, naturalHeight } = e.currentTarget;
                                    if (
                                        naturalWidth < MIN_DIMENSION ||
                                        naturalHeight < MIN_DIMENSION
                                    ) {
                                        setError("Image must be at least 150 x 150 pixels.");
                                        return setImgSrc("");
                                    }
                                });
                                setImgSrc(imageUrl);
                                // Open modal only if shouldCrop is true
                                if (shouldCrop) {
                                    setModalOpen(true);
                                } else {
                                    onFileUpload(
                                        {
                                            url: imageUrl,
                                            name: file.name,
                                            size: formatFileSize(file.size),
                                            type: file.type
                                        },
                                        file
                                    );
                                }
                            });
                            reader.readAsDataURL(file);
                        }
                    }
                },
                customErrorMessage: `Please upload a valid ${fileType.toLowerCase()} file. Supported formats: ${allowedMediaExtensions[
                    fileType
                ].join(", ")}, Max size: ${(allowedMediaSize[fileType] / 1024).toFixed(2)}MB.`
            });

            event.target.value = null;
        }
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const cropWidthInPercent = (MIN_DIMENSION / width) * 100;

        const crop = makeAspectCrop(
            {
                unit: "%",
                width: cropWidthInPercent
            },
            ASPECT_RATIO,
            width,
            height
        );
        const centeredCrop = centerCrop(crop, width, height);
        setCrop(centeredCrop);
    };

    const handleCrop = () => {
        // Use the canvas to get the cropped image
        setCanvasPreview(
            imgRef.current, // HTMLImageElement
            previewCanvasRef.current, // HTMLCanvasElement
            convertToPixelCrop(crop, imgRef.current.width, imgRef.current.height)
        );
        const dataUrl = previewCanvasRef.current.toDataURL("image/webp", 0.7);

        // Convert the data URL to a Blob object to get the size
        fetch(dataUrl)
            .then((res) => res.blob())
            .then((blob) => {
                const croppedImageSizeInKB = Number((blob.size / 1024).toFixed(2)); // Size in KB, rounded to 2 decimal places

                // Update the uploaded file with the cropped image and its size
                setUploadedFile({
                    url: dataUrl, // The cropped image URL
                    name: uploadedFile.name,
                    size: croppedImageSizeInKB // The size of the cropped image in KB
                });

                // Close the modal
                setModalOpen(false);
                setIsCropConfirmed(true); // Set the crop as confirmed

                // Pass the cropped image (or original) to the parent
                onFileUpload(
                    {
                        url: dataUrl, // Cropped image URL
                        name: uploadedFile.name,
                        size: croppedImageSizeInKB, // The size of the cropped image
                        type: uploadedFile.type
                    },
                    {
                        name: uploadedFile.name, // From the original uploadedFile (File object)
                        size: blob.size, // The size of the cropped image in bytes
                        type: uploadedFile.type, // MIME type
                        stream: blob // The actual data stream as a Blob
                    }
                );
            });
    };

    // When modal is closed without cropping, pass the original file to the parent
    const handleModalClose = () => {
        if (!isCropConfirmed) {
            onFileUpload(
                {
                    url: uploadedFile.url, // Original file URL
                    name: uploadedFile.name,
                    size: uploadedFile.size, // Original file size
                    type: uploadedFile.type
                },
                file
            );
        }
        setModalOpen(false);
    };

    return (
        <>
            <FormGroup className="file-uploader">
                {uploadedFile ? (
                    <div className="uploaded-file-container d-flex align-items-center justify-content-between">
                        <div className="file-preview d-flex align-items-center">
                            {uploadedFile.type?.startsWith("image/") ||
                            fileType === MediaType.IMAGE ? (
                                <img
                                    src={uploadedFile.url}
                                    alt={uploadedFile.name}
                                    className="uploaded-thumbnail"
                                />
                            ) : (
                                <audio controls className="uploaded-audio">
                                    <source src={uploadedFile.url} type="audio/mpeg" />
                                </audio>
                            )}
                            <div className="file-details">
                                <span className="file-name">
                                    {truncateFileName(uploadedFile.name.split("__").pop(), 10)}{" "}
                                </span>
                                <span className="file-size">{uploadedFile.size} KB</span>{" "}
                            </div>
                        </div>
                        <button
                            className="remove-file-btn"
                            onClick={removeFile}
                            type="button"
                            aria-label="Remove file"
                        >
                            <img src={modalCloseIcon} alt="Remove file" />
                        </button>
                    </div>
                ) : (
                    <div
                        {...getRootProps()}
                        className={`file-upload-box ${isDragActive ? "drag-active" : ""}`}
                    >
                        <input {...getInputProps()} />
                        <div className="upload-area">
                            <img src={uploadFileIcon} alt="Upload icon" />
                            <div className="upload-text">
                                {isDragActive ? "Drop the file here..." : uploadText}
                                <span className="browse-link">Browse file</span>
                            </div>
                            <div className="upload-subtext">{supportedFormats}</div>
                        </div>
                    </div>
                )}
                {error && <div className="error-text text-danger">{error}</div>}
            </FormGroup>

            {shouldCrop && imgSrc && (
                <CustomModal
                    isOpen={modalOpen}
                    size="lg"
                    className="filter-modal"
                    modalBodyClasses="d-flex justify-content-center"
                    customButton={false}
                    modalTitle={<h2 className="m-0 px-2">Crop</h2>}
                    handleModalToggling={handleModalClose}
                >
                    <>
                        <div className="d-flex align-items-center">
                            <ReactCrop
                                crop={crop}
                                onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
                                circularCrop
                                keepSelection
                                aspect={ASPECT_RATIO}
                                minWidth={MIN_DIMENSION}
                            >
                                <img
                                    ref={imgRef}
                                    src={imgSrc}
                                    alt="Upload"
                                    style={{ maxHeight: "70vh" }}
                                    onLoad={onImageLoad}
                                />
                            </ReactCrop>
                        </div>
                        <CustomButton
                            title={"Crop Image"}
                            color="primary"
                            className="shadow-theme grad-primary mt-4"
                            onClick={handleCrop}
                        />
                    </>

                    {crop && (
                        <canvas
                            ref={previewCanvasRef}
                            className="mt-4"
                            style={{
                                display: "none",
                                border: "1px solid black",
                                objectFit: "contain",
                                width: 150,
                                height: 150
                            }}
                        />
                    )}
                </CustomModal>
            )}
        </>
    );
};

export default FileUploader;
